import React, { FC, Fragment, useEffect, useState } from 'react';
import { Grid, Box, Button, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import InvoiceFilterSelect from '../invoice-filter-select/invoice-filter-select.component';
import ClinicFilter from 'components/shared/clinic-filter.component';
import DentistClinicFilter from 'components/shared/denitist-clinic-filter/denitist-clinic-filter.component';
import { DateRange } from '@mui/lab';
import DateRangeFilter from '../date-range-filter/date-range-filter.component';
import InvoiceFilterFreeText from '../invoice-filter-free-text/invoice-filter-free-text.component';
import { useTranslation } from 'react-i18next';
import {
    InvoiceDate,
    InvoiceDeliveryMethod,
    InvoiceFilter,
    InvoiceStatus,
    InvoiceType,
    CertificationStatus,
    InvoiceListType,
} from 'models/invoice';
import { permissions } from 'utils/permission';
import { currentUserVar } from 'apollo/reactive-variables/user';
import LabFilter from '../invoice-filter-lab/invoice-filter-lab.component';
import { Organization, ConnectedLab } from 'models/organization';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type InvoicesPageActionsProps = {
    onInvoiceFilterChange: (invoiceFilter: InvoiceFilter) => void;
    filters: InvoiceFilter;
    onExcelInvoiceFilterChange: (invoiceFilter: InvoiceFilter) => void;
};

const InvoicesPageActions: FC<InvoicesPageActionsProps> = ({
    onInvoiceFilterChange,
    filters,
    onExcelInvoiceFilterChange,
}: InvoicesPageActionsProps) => {
    const { t: translate } = useTranslation(['invoices', 'orders', 'common']);
    const currentUser = currentUserVar();
    const currentOrganization = currentOrgVar();

    const [invoiceFilter, setInvoiceFilter] = useState<InvoiceFilter>();
    const [invoiceStatus, setInvoiceStatus] = useState(InvoiceStatus.ALL);
    const [listType, setListType] = useState(InvoiceListType.MY_INVOICES.toString());

    const isCertificationEnabled = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CERTIFICATION,
    );

    const isEmpty = (obj: InvoiceFilter) => {
        return Object.entries(obj).length === 0;
    };

    useEffect(() => {
        setInvoiceFilter(filters);
        if (!isEmpty(filters) && filters.myInvoice) {
            setListType(InvoiceListType.MY_INVOICES.toString());
        } else if (!isEmpty(filters) && filters.myInvoice === false) {
            setListType(InvoiceListType.ALL_INVOICES.toString());
        } else {
            setListType(InvoiceListType.MY_INVOICES.toString());
        }
        if (filters.invoiceStatus) {
            setInvoiceStatus(filters.invoiceStatus);
        }
    }, [filters]);

    const freeTextHandler = (invoiceNumber: string) => {
        setInvoiceFilter({ ...invoiceFilter, invoiceNumber });
    };

    const clinicSelectHandler = (_: React.SyntheticEvent, clinic: Organization | null) => {
        setInvoiceFilter({ ...invoiceFilter, clinicOrganizationId: clinic?._id as string, clinic });
    };

    const onLabFilterChange = (_: React.SyntheticEvent, lab: ConnectedLab | null) => {
        setInvoiceFilter({ ...invoiceFilter, labId: lab?.lab?._id as string, lab });
    };

    const invoiceStatusSelectHandler = (invoiceStatusKey: string) => {
        const statusFilter = Object.entries(InvoiceStatus).find(([key]) => {
            return key === invoiceStatusKey;
        });
        const filter: InvoiceFilter = {
            ...invoiceFilter,
            invoiceStatus: statusFilter ? statusFilter[1] : InvoiceStatus.ALL,
        };
        if (invoiceStatusKey === InvoiceStatus.ALL) {
            delete filter.invoiceStatus;
        }
        setInvoiceStatus(invoiceStatusKey as InvoiceStatus);
        setInvoiceFilter(filter);
    };

    const invoiceTypeSelectHandler = (invoiceType: string) => {
        const filter: InvoiceFilter = { ...invoiceFilter, invoiceType };
        if (invoiceType === InvoiceType.ALL) {
            delete filter.invoiceType;
        }
        setInvoiceFilter(filter);
    };

    const invoiceDeliverySelectHandler = (deliveryMethod: string) => {
        const filter: InvoiceFilter = { ...invoiceFilter, deliveryMethod };
        if (deliveryMethod === InvoiceDeliveryMethod.ALL) {
            delete filter.deliveryMethod;
        }
        setInvoiceFilter(filter);
    };

    const certificationStatusSelectHandler = (certificationStatus: string) => {
        const filter: InvoiceFilter = { ...invoiceFilter, certificationStatus };
        if (certificationStatus === CertificationStatus.ALL) {
            delete filter.certificationStatus;
        }
        setInvoiceFilter(filter);
    };

    const invoiceDateSelectHandler = (dateFilterType: string) => {
        const filter: InvoiceFilter = { ...invoiceFilter, dateFilterType };
        if (dateFilterType === InvoiceDate.NO_DATE_FILTER) {
            delete filter.dateFilterType;
            delete filter.dateFromTo;
        }
        setInvoiceFilter(filter);
    };

    const dateRangeFilterHandler = (dateFromTo: DateRange<Date>) => {
        const updatedFilter = { ...invoiceFilter, dateFromTo };
        setInvoiceFilter(updatedFilter);
        const isDateRangeCleared = !dateFromTo[0] && !dateFromTo[1];
        if (isDateRangeCleared) {
            const { dateFromTo: _removed, ...filterWithoutDateRange } = updatedFilter;
            onInvoiceFilterChange(filterWithoutDateRange);
        }
    };

    const yourReferenceHandler = (yourReference: string) => {
        setInvoiceFilter({ ...invoiceFilter, yourReference });
    };

    const filter = () => {
        onInvoiceFilterChange(invoiceFilter || {});
    };

    const excelFilter = () => {
        onExcelInvoiceFilterChange(invoiceFilter || {});
    };

    const handleListTypeChange = (event: SelectChangeEvent) => {
        const listTypeValue = event.target.value;
        setListType(listTypeValue);
        setInvoiceFilter({ ...invoiceFilter, myInvoice: listTypeValue === InvoiceListType.MY_INVOICES.toString() });
    };

    return (
        <Fragment>
            <Box
                sx={{
                    marginTop: 1,
                    marginBottom: 2,
                    bgcolor: 'background.paper',
                    padding: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Grid container rowSpacing={1.5} columnSpacing={1}>
                    {permissions.isDentist(currentUser.type) ? (
                        <Grid item md={2} sm={6} xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="invoice-select-label">{translate('invoices:INVOICES')}</InputLabel>
                                <Select
                                    value={listType}
                                    onChange={handleListTypeChange}
                                    labelId="clinic-select-label"
                                    label={translate('invoices:INVOICES')}
                                >
                                    <MenuItem value={InvoiceListType.MY_INVOICES.toString()}>
                                        {translate('invoices:MY_INVOICES')}
                                    </MenuItem>
                                    <MenuItem value={InvoiceListType.ALL_INVOICES.toString()}>
                                        {translate('invoices:ALL_INVOICES')}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    ) : undefined}
                    <Grid item xs={2}>
                        <InvoiceFilterFreeText
                            initialValue={invoiceFilter?.invoiceNumber}
                            label={'INVOICE_NUMBER'}
                            onFreeTextChange={freeTextHandler}
                        />
                    </Grid>
                    {permissions.isDentist(currentUser?.type) ? (
                        <Grid item xs={4}>
                            <DentistClinicFilter
                                selectedClinic={invoiceFilter?.clinic}
                                onClinicFilterChange={clinicSelectHandler}
                            />
                        </Grid>
                    ) : undefined}
                    {permissions.isLabTechnician(currentUser.type) ? (
                        <Grid item xs={4}>
                            <ClinicFilter
                                selectedClinic={invoiceFilter?.clinic}
                                onClinicFilterChange={clinicSelectHandler}
                                showParents
                            />
                        </Grid>
                    ) : undefined}
                    {!permissions.isLabTechnician(currentUser.type) ? (
                        <Grid item xs={4}>
                            <LabFilter selectedLab={invoiceFilter?.lab} onLabFilterChange={onLabFilterChange} />
                        </Grid>
                    ) : undefined}
                    <Grid item xs={2}>
                        <InvoiceFilterSelect
                            label="invoices:INVOICE_STATUS"
                            menuItems={Object.keys(InvoiceStatus)}
                            optionSelected={invoiceStatus}
                            onSelectFilterChange={invoiceStatusSelectHandler}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <InvoiceFilterSelect
                            label="invoices:INVOICE_TYPE"
                            menuItems={Object.values(InvoiceType)}
                            optionSelected={invoiceFilter?.invoiceType || InvoiceType.ALL}
                            onSelectFilterChange={invoiceTypeSelectHandler}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <InvoiceFilterSelect
                            label="invoices:DELIVERY_METHOD"
                            menuItems={Object.values(InvoiceDeliveryMethod)}
                            optionSelected={invoiceFilter?.deliveryMethod || InvoiceDeliveryMethod.ALL}
                            onSelectFilterChange={invoiceDeliverySelectHandler}
                        />
                    </Grid>

                    {isCertificationEnabled && permissions.isDentist(currentUser.type) ? (
                        <Grid item xs={2}>
                            <InvoiceFilterSelect
                                translationNamespace="orders"
                                label="orders:CERTIFICATION"
                                menuItems={Object.values(CertificationStatus)}
                                optionSelected={invoiceFilter?.certificationStatus || CertificationStatus.ALL}
                                onSelectFilterChange={certificationStatusSelectHandler}
                            />
                        </Grid>
                    ) : undefined}
                    <Grid item xs={2}>
                        <InvoiceFilterSelect
                            label="orders:FILTER_BY_DATE"
                            menuItems={Object.values(InvoiceDate)}
                            optionSelected={invoiceFilter?.dateFilterType || InvoiceDate.NO_DATE_FILTER}
                            onSelectFilterChange={invoiceDateSelectHandler}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <DateRangeFilter
                            dateFilterType={invoiceFilter?.dateFilterType || InvoiceDate.NO_DATE_FILTER}
                            dateRangeValue={invoiceFilter?.dateFromTo || [null, null]}
                            onDateRangeFilterChange={dateRangeFilterHandler}
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <InvoiceFilterFreeText
                            initialValue={invoiceFilter?.yourReference}
                            label={'YOUR_REFERENCE'}
                            onFreeTextChange={yourReferenceHandler}
                        />
                    </Grid>

                    <Grid item xs={4} sx={{ textAlign: 'right' }}>
                        {permissions.isLabTechnician(currentUser.type) ? (
                            <Button onClick={excelFilter} size="medium" type="button" startIcon={<FileDownloadIcon />}>
                                {translate('common:EXPORT_EXCEL')}
                            </Button>
                        ) : undefined}
                        <Button onClick={filter} size="medium" type="button" startIcon={<RefreshIcon />}>
                            {translate('common:REFRESH_LIST')}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
        </Fragment>
    );
};

export default InvoicesPageActions;
